const ENTRY_SEPARATOR = /\r?\n|[,;]+/;

export const normalizeNormalRange = (value = "") => {
  if (value == null) return "";

  return String(value)
    .replace(/\r\n?/g, "\n")
    .split("\n")
    .map((line) => line.trim())
    .join("\n");
};

export const getTrailingBlankLineCount = (normalRange) => {
  const lines = String(normalRange ?? "")
    .replace(/\r\n?/g, "\n")
    .split("\n");

  let count = 0;
  for (let index = lines.length - 1; index > 0; index -= 1) {
    if (lines[index].trim() !== "") break;
    count += 1;
  }

  return count;
};

export const getNormalRangeBottomPadding = (normalRange) => {
  const blankLineCount = getTrailingBlankLineCount(normalRange);
  return blankLineCount > 0 ? `${blankLineCount * 1.2}em` : undefined;
};

export const splitNormalRangeEntries = (normalRange) =>
  normalizeNormalRange(normalRange)
    .split(ENTRY_SEPARATOR)
    .map((entry) => entry.trim())
    .filter(Boolean);

const isMaleEntry = (entry) =>
  /\bmale\b/i.test(entry) || /^\s*m\s*[:=(]/i.test(entry);

const isFemaleEntry = (entry) =>
  /\bfemale\b/i.test(entry) || /^\s*f\s*[:=(]/i.test(entry);

const removeGenderLabel = (entry) =>
  entry
    .replace(/^\s*(?:male|female|m|f)\s*[:=-]?\s*/i, "")
    .replace(/\s*\((?:male|female|m|f)\)\s*/gi, "")
    .trim();

export const getGenderSpecificRange = (normalRange, gender) => {
  const normalizedRange = normalizeNormalRange(normalRange);
  if (!normalizedRange || !gender) return normalizedRange;

  const entries = splitNormalRangeEntries(normalizedRange);
  const normalizedGender = String(gender).trim().toLowerCase();
  const matcher = normalizedGender.startsWith("f")
    ? isFemaleEntry
    : normalizedGender.startsWith("m")
      ? isMaleEntry
      : null;

  if (!matcher) return normalizedRange;

  const matchingEntry = entries.find(matcher);
  return matchingEntry ? removeGenderLabel(matchingEntry) : normalizedRange;
};

const parseNumericRange = (normalRange) => {
  const range = normalizeNormalRange(normalRange);
  if (!range || range.includes("\n")) return null;

  const boundedMatch = range.match(
    /^\s*(-?\d+(?:\.\d+)?)\s*[-–—]\s*(-?\d+(?:\.\d+)?)(?:\s+.*)?$/
  );
  if (boundedMatch) {
    return {
      type: "bounded",
      min: Number(boundedMatch[1]),
      max: Number(boundedMatch[2]),
    };
  }

  const comparisonMatch = range.match(/^\s*(<=?|>=?)\s*(-?\d+(?:\.\d+)?)/);
  if (comparisonMatch) {
    return {
      type: "comparison",
      operator: comparisonMatch[1],
      limit: Number(comparisonMatch[2]),
    };
  }

  return null;
};

export const getLabValueColor = (value, normalRange, gender) => {
  const numericValue = Number.parseFloat(value);
  if (!Number.isFinite(numericValue)) return "inherit";

  const applicableRange = getGenderSpecificRange(normalRange, gender);
  const parsedRange = parseNumericRange(applicableRange);
  if (!parsedRange) return "inherit";

  if (parsedRange.type === "bounded") {
    return numericValue < parsedRange.min || numericValue > parsedRange.max
      ? "#FF4444"
      : "#2ECC71";
  }

  const { operator, limit } = parsedRange;
  // Preserve the legacy boundary behavior: values equal to a < or > limit
  // were treated as normal by the existing report templates.
  const isNormal = operator.startsWith("<")
    ? numericValue <= limit
    : numericValue >= limit;

  return isNormal ? "#2ECC71" : "#FF4444";
};
